import {
  Box,
  Typography,
  CollapseProps,
  Stack,
  Card,
  CardContent,
  Collapse,
  styled,
  Button,
  CardActions,
  CircularProgress,
} from '@mui/material';

import {
  Calendar as CalendarToday,
  Trash2 as Delete,
  Download,
  ChevronDown as ExpandMore,
  CircleUser as Person,
  Video as Videocam,
  SquareArrowUpRight as Export,
  X as Cancel,
  Edit,
  Play,
  Hash,
  RotateCcw as Retry,
} from 'lucide-react';
import { AUTO_DIRECTOR_CAMERA_LABEL, ClipModel, ClipStatus } from '../types';
import { TeamModel } from '@/features/teams/types/TeamModel';
import { useEffect, useMemo, useState } from 'react';
import { parseDate } from '@c360/shared';
import ClipStatusComponent from '@/shared/components/ClipStatus';
import useCognito from '@/features/authentication/hooks/useCognito';
import { Tracking } from '@/shared/services/tracking';
import DownloadClipDialog from '@/shared/components/DownloadClipDialog';
import TrackedObject from '@/features/teams/components/TrackedObject';

interface Props {
  clip: ClipModel;
  isEditingSomeOtherClip: boolean;
  collapseProps?: CollapseProps;
  clipDescription?: string;
  displayDescription: boolean;
  teams?: TeamModel[];
  getCameraNameByCameraId: (cameraId: number) => string;
  sx?: any;
  onDelete?: () => Promise<void>;
}

const ExpandIcon = styled(ExpandMore)(({ isexpanded }: { isexpanded: boolean }) => ({
  transition: 'transform 0.2s ease',
  transform: isexpanded ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const ClipRecord = ({ clip, teams, getCameraNameByCameraId, sx, onDelete }: Props) => {
  const cognito = useCognito();

  const [isExpanded, setIsExpanded] = useState(false);
  const [pendingAction, setPendingAction] = useState<'export' | 'cancel' | 'delete' | null>(null);

  // Clear pending state when clip status changes (subscription update received)
  useEffect(() => {
    if (pendingAction) {
      const statusUpper = clip.status.toUpperCase() as ClipStatus;

      // Clear pending state based on status transitions
      if (pendingAction === 'export' && [ClipStatus.PENDING, ClipStatus.PROCESSING].includes(statusUpper)) {
        setPendingAction(null);
      } else if (pendingAction === 'cancel' && [ClipStatus.CANCELED, ClipStatus.ERROR].includes(statusUpper)) {
        setPendingAction(null);
      } else if (pendingAction === 'delete') {
        // Delete will unmount component, but clear anyway
        setPendingAction(null);
      }
    }
  }, [clip.status, pendingAction]);

  const [downloadClipDialogOpen, setDownloadClipDialogOpen] = useState(false);

  const cancelClipHandler = async () => {
    setPendingAction('cancel');
    Tracking.getInstance().track('Cancel', {
      category: 'Clips',
      type: 'clip',
    });
  };

  const handleDeleteClip = async () => {
    setPendingAction('delete');
    Tracking.getInstance().track('Delete', {
      category: 'Clips',
      type: 'clip',
    });

    if (onDelete) {
      await onDelete();
    }
  };

  const handleEditClip = async () => {
    Tracking.getInstance().track('Edit', {
      category: 'Clips',
      type: 'clip',
    });
  };

  const handleDownloadClip = () => {
    setDownloadClipDialogOpen(true);
  };

  const exportClipHandler = async () => {
    setPendingAction('export');
    Tracking.getInstance().track('Export', {
      category: 'Clips',
      type: 'clip',
    });
  };

  const isOwnClip = cognito.userAttributes?.email === clip.clouduser?.email;

  const formatDate = (d: string | Date) => {
    if (!d) return '';
    const date = parseDate(d);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const buttonConditions = {
    cancel: [ClipStatus.PROCESSING, ClipStatus.PENDING, ClipStatus.CANCELING, ClipStatus.INITIALIZING].includes(
      clip.status.toUpperCase() as ClipStatus,
    ),
    edit: isOwnClip && clip.status.toUpperCase() !== ClipStatus.READY,
    export:
      [ClipStatus.CANCELED, ClipStatus.NEW].includes(
        clip.status.toUpperCase() as ClipStatus,
      ),
    retry: clip.status.toUpperCase() === ClipStatus.ERROR,
    download: clip.status.toUpperCase() === ClipStatus.READY,
    delete: isOwnClip,
  };

  const cameraName = useMemo(() => {
    if (clip.autoDirector) return AUTO_DIRECTOR_CAMERA_LABEL;
    const cameraId =
      typeof clip.cameraId === 'string' ? parseInt(clip.cameraId, 10) : clip.cameraId;
    return getCameraNameByCameraId(cameraId) || 'Unknown Camera';
  }, [clip.autoDirector, clip.cameraId, getCameraNameByCameraId]);

  return (
    <>
      <DownloadClipDialog
        open={downloadClipDialogOpen}
        clip={clip as ClipModel}
        onClose={() => setDownloadClipDialogOpen(false)}
      />

      <Card
        data-clip-id={clip.id}
        sx={{
          ...sx,
          width: '100%',
          // Dark card styling now comes from theme automatically
        }}
      >
        <CardContent>
          {/* Header Section */}
              <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ gap: 0.75, display: 'flex', alignItems: 'center' }}>
                  <Hash size={14} color="#737373" />
                  <Typography variant="caption" className="card-header">
                    CLIP_{clip.id}
                  </Typography>
                </Box>
                <Box sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
            
                  <ClipStatusComponent status={clip.status} />
                </Box>
              </Box>

              {/* Main Content */}
              <Stack spacing={2}>


                {/* Tracked Object - Only show when in tracked mode */}
                {(!clip.cameraMode || clip.cameraMode === 'tracked') && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#525252',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.68rem',
                        display: 'block',
                        mb: 0.25,
                      }}
                    >
                      Tracked object
                    </Typography>

                    <TrackedObject value={clip.objectTrackingId} teams={teams} readOnly={true} sx={{pl:4}} />
                  </Box>
                )}

                {/* PTZ Values - Show when in fixed camera mode */}
                {clip.cameraMode === 'fixed' && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#525252',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.68rem',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Fixed Camera Position (PTZ)
                    </Typography>
                    <Box sx={{ gap: 2, pl: 0, display: 'flex' }}>
                      <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: '0.8rem' }}>
                        Pan: {clip.pan !== undefined ? clip.pan.toFixed(2) : 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: '0.8rem' }}>
                        Tilt: {clip.tilt !== undefined ? clip.tilt.toFixed(2) : 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: '0.8rem' }}>
                        Zoom: {clip.zoom !== undefined ? clip.zoom.toFixed(2) : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Camera Info */}
                <Box sx={{ gap: 1.5, display: 'flex', alignItems: 'flex-start' }}>
                  <Videocam size={18} color="#525252" />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#525252',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.68rem',
                        display: 'block',
                        mb: 0.25,
                      }}
                    >
                      Camera
                    </Typography>
                    <Typography variant="body2" className="card-content">
                      {cameraName}
                    </Typography>
                  </Box>
                </Box>


                {/* Expand/Collapse Details Button */}
                <Button
                  onClick={() => setIsExpanded(!isExpanded)}
                  endIcon={<ExpandIcon isexpanded={isExpanded} size={14} />}
                  fullWidth
                  sx={{
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                    color: '#737373',
                    border: 'none',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                    py: 1
                  }}
                >
                  {isExpanded ? 'Hide' : 'Details'}
                </Button>

                {/* Collapsible Details */}
                <Collapse in={isExpanded} timeout={300}>
                  <Stack
                    spacing={2}
                    sx={{
                      pt: 2,
                      borderTop: '1px solid #262626',
                      mt: 2,
                    }}
                  >
                    {/* Creator */}
                    <Box sx={{ gap: 1.5, display: 'flex', alignItems: 'flex-start' }}>
                      <Person size={14} color="#525252" style={{ marginTop: 2 }} />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#525252',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: '0.68rem',
                            display: 'block',
                            mb: 0.25,
                          }}
                        >
                          Creator
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#a3a3a3',
                            fontSize: '0.8rem',
                            wordBreak: 'break-all',
                          }}
                        >
                          {clip.clouduser?.email || 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Created Date */}
                    <Box sx={{ gap: 1.5, display: 'flex', alignItems: 'flex-start' }}>
                      <CalendarToday size={14} color="#525252" style={{ marginTop: 2 }} />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#525252',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: '0.68rem',
                            display: 'block',
                            mb: 0.25,
                          }}
                        >
                          Created
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#a3a3a3',
                            fontSize: '0.8rem',
                          }}
                        >
                          {formatDate(clip.createdAt)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Description */}
                    {clip.description && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#525252',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: '0.68rem',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          Description
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#a3a3a3',
                            fontSize: '0.8rem',
                            lineHeight: 1.5,
                          }}
                        >
                          {clip.description}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Collapse>
              </Stack>
        </CardContent>

        <CardActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 0.5,
            borderTop: '1px solid #262626',
            py: 1.5,
            px: 2,
            backgroundColor: '#050505',
          }}
        >
            {/* Play Button on the left */}
            <Button
              onClick={(e) => {
                e.stopPropagation();
              }}
              startIcon={<Play size={16} strokeWidth={1.5} />}
              size="small"
              sx={{
                minWidth: 'auto',
                p: 1,
                color: '#737373',
                backgroundColor: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: '#a3a3a3',
                },
              }}
            >
              Play
            </Button>

            {/* Action buttons on the right */}
            <Box sx={{ gap: 0.5, display: 'flex' }}>
              {[
                {
                  icon: Cancel,
                  label: 'Cancel',
                  color: 'warning',
                  variant: 'outlined' as const,
                  condition: buttonConditions.cancel,
                  handler: cancelClipHandler,
                  actionType: 'cancel' as const,
                },
                {
                  icon: Export,
                  label: 'Export',
                  color: 'primary',
                  condition: buttonConditions.export,
                  handler: exportClipHandler,
                  actionType: 'export' as const,
                },
                {
                  icon: Retry,
                  label: 'Retry',
                  color: 'warning',
                  condition: buttonConditions.retry,
                  handler: exportClipHandler,
                  actionType: 'export' as const,
                },
                {
                  icon: Download,
                  label: 'Download',
                  color: 'success',
                  condition: buttonConditions.download,
                  handler: handleDownloadClip,
                  actionType: null,
                },
                {
                  icon: Edit,
                  label: 'Edit',
                  color: 'primary',
                  condition: buttonConditions.edit,
                  handler: handleEditClip,
                  actionType: null,
                },
                {
                  icon: Delete,
                  label: 'Delete',
                  color: 'error',
                  condition: buttonConditions.delete,
                  handler: handleDeleteClip,
                  actionType: 'delete' as const,
                },
              ]
                .filter((button) => button.condition)
                .map((button) => {
                  const Icon = button.icon;
                  const isPending = button.actionType && pendingAction === button.actionType;
                  const isDisabled = !!pendingAction;

                  return (
                    <Button
                      key={button.label}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (button.handler && !isDisabled) button.handler();
                      }}
                      size="small"
                      variant={button.variant ? button.variant : 'contained'}
                      startIcon={
                        isPending ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Icon size={16} strokeWidth={1.5} />
                        )
                      }
                      color={button.color as any}
                      disabled={isDisabled}
                      sx={{
                        minWidth: 'auto',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {button.label}
                    </Button>
                  );
                })}
            </Box>
        </CardActions>
      </Card>
    </>
  );
};

export default ClipRecord;
